/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import {
	Avatar,
	Badge,
	Box,
	Button,
	Center,
	Checkbox,
	CheckboxGroup,
	Container,
	Divider,
	Flex,
	HStack,
	Radio,
	RadioGroup,
	SimpleGrid,
	Stack,
	Text,
	useDisclosure,
	useToast,
	WrapItem,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLongArrowAltRight, faMinusSquare, faPlusSquare } from '@fortawesome/free-solid-svg-icons';
import CompanyName from '../../components/CompanyName';
import TransferEwsItem from './TransferEwsItem';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import SelectUserModal from '../../components/Modal/SelectUserModal';
import useStore from '../../store';
import c from '../../constant';

const Q_USER = gql`
	query get($id: String!) {
		getUserById(id: $id) {
			id
			fullName
			fullNameKhmer
			avatar
		}
	}
`;

const Q_STATEMENTS = gql`
	query get($companyTypes: [InsuranceInstitutionsType!] = [], $assigneeId: String!) {
		getAllEarlyWarningStatementsAssignedToAssigneeId(companyTypes: $companyTypes, assigneeId: $assigneeId)
	}
`;

function TransferEwsReport() {
	const toast = useToast();
	const location = useLocation();
	const modalUser = useDisclosure();
	const urlParams = new URLSearchParams(location.search);
	const id = urlParams.get('id') || null;
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const [transferee, setTransferee] = useState(null);
	const [companyTypes, setCompanyTypes] = useState([]);
	const [notification, setNotification] = useState('none');
	const [list, setList] = useState([]);
	const [transferIndex, setTransferIndex] = useState(0);
	const [assigning, setAssigning] = useState(false);
	const [fetchUser, { data: dataUser }] = useLazyQuery(Q_USER);
	const [fetchStatements, { data: dataStatement }] = useLazyQuery(Q_STATEMENTS);

	useEffect(() => id && loadUser(), [id]);

	useEffect(() => transferee && loadStatement(), [companyTypes, transferee]);

	useEffect(() => setList(dataStatement?.getAllEarlyWarningStatementsAssignedToAssigneeId), [dataStatement]);

	function loadUser() {
		fetchUser({ variables: { id } });
	}

	function loadStatement() {
		fetchStatements({ variables: { companyTypes, assigneeId: dataUser?.getUserById?.id } });
	}

	return (
		<>
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{
							name: t('Users'),
							path: '/users?page=1',
						},
						{
							name: t('Transfer Early Warning Report'),
							path: '#',
						},
					]}
				/>
				<Center mt="5">
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						pb="32px"
					>
						<Flex bg="#FAFAFA" p="16px" pt="8px" pb="8px" mb="16px" justifyContent="space-between" borderBottom="1px solid #dbdbdb">
							<Text fontSize="x-large" fontWeight="bold">
								{t('Transfer Early Warning Report')}
							</Text>
						</Flex>
						{dataUser?.getUserById?.id && (
							<Box p="16px">
								<Flex justifyContent="center" alignItems="center">
									<Flex w="150px" justifyContent="center" alignItems="center" direction="column">
										<Avatar
											name={
												lang === 'kh'
													? dataUser?.getUserById?.fullNameKhmer || dataUser?.getUserById?.fullName || dataUser?.getUserById?.username
													: dataUser?.getUserById?.fullName || dataUser?.getUserById?.fullNameKhmer || dataUser?.getUserById?.username
											}
											src={dataUser?.getUserById?.avatar?.icon ? c.baseFilePath + dataUser?.getUserById?.avatar?.icon : null}
										/>
										<Text fontWeight="bold" mt={1}>
											{lang === 'kh'
												? dataUser?.getUserById?.fullNameKhmer || dataUser?.getUserById?.fullName || dataUser?.getUserById?.username
												: dataUser?.getUserById?.fullName || dataUser?.getUserById?.fullNameKhmer || dataUser?.getUserById?.username}
										</Text>
									</Flex>
									<FontAwesomeIcon icon={faLongArrowAltRight} style={{ fontSize: 32, marginLeft: 32, marginRight: 32, color: '#2980b9' }} />
									<Flex w="150px" justifyContent="center" alignItems="center" direction="column">
										{transferee ? (
											<>
												<WrapItem className="avatarWraper">
													{assigning === false && (
														<FontAwesomeIcon
															onMouseDown={() => setTransferee(null)}
															className="remove"
															icon={faMinusSquare}
															style={{
																fontSize: 18,
																display: 'none',
																position: 'absolute',
																zIndex: '100',
																marginLeft: 32,
																color: 'red',
																backgroundColor: 'white',
																padding: 2,
																cursor: 'pointer',
															}}
														/>
													)}
													<Avatar
														name={
															lang === 'kh'
																? transferee?.fullNameKhmer || transferee?.fullName || transferee?.username
																: transferee?.fullName || transferee?.fullNameKhmer || transferee?.username
														}
														src={transferee?.avatar?.icon ? c.baseFilePath + transferee?.avatar?.icon : null}
													/>
												</WrapItem>
												<Text fontWeight="bold" mt={1}>
													{lang === 'kh'
														? transferee?.fullNameKhmer || transferee?.fullName || transferee?.username
														: transferee?.fullName || transferee?.fullNameKhmer || transferee?.username}
												</Text>
											</>
										) : (
											<Button
												onMouseDown={modalUser?.onOpen}
												colorScheme="blue"
												variant="outline"
												leftIcon={<FontAwesomeIcon icon={faPlusSquare} style={{ fontSize: 18 }} />}
											>
												{t('Select')}
											</Button>
										)}
									</Flex>
								</Flex>
								<>
									{transferee && (
										<Flex mt="4" justifyContent="center" alignItems="center" direction="column">
											<Stack spacing={4} direction="column">
												<CheckboxGroup isDisabled={assigning} colorScheme="blue" defaultValue={companyTypes} onChange={setCompanyTypes}>
													<Stack spacing={2} direction="column">
														<Checkbox value="GENERAL_INSURANCE">{t('General Insurance')}</Checkbox>
														<Checkbox value="LIFE_INSURANCE">{t('Life Insurance')}</Checkbox>
														<Checkbox value="MICRO_INSURANCE">{t('Micro Insurance')}</Checkbox>
														<Checkbox value="REINSURANCE">{t('ReInsurance')}</Checkbox>
													</Stack>
												</CheckboxGroup>
												{list?.length > 0 && (
													<HStack spacing={4}>
														<Text fontWeight="500" fontSize="md">
															{t('Notification')}
														</Text>
														<RadioGroup isDisabled={assigning} value={notification} onChange={setNotification}>
															<HStack spacing={4}>
																<Radio value="none">{t('None')}</Radio>
																<Radio value="all">{t('All')}</Radio>
															</HStack>
														</RadioGroup>
													</HStack>
												)}
												<Button
													onMouseDown={() => {
														setAssigning(true);
														setTransferIndex(transferIndex + 1);
													}}
													colorScheme="blue"
													variant="solid"
													isDisabled={list?.length > 0 && false === assigning ? false : true}
												>
													{list?.length > 0 ? `${t('Transfer')} ${list?.length} ${t('Report(s)')}` : t('Transfer')}
												</Button>
											</Stack>
										</Flex>
									)}
								</>
								<>
									<Divider mt={6} mb={6} />
								</>
								<>
									{transferee && list?.length > 0 && (
										<SimpleGrid w="100%" columns={[1, 2, 3]} spacing={[1, 2, 4]}>
											{list?.map((item, index) => (
												<Box key={index} cursor="pointer" _hover={{ backgroundColor: 'gray.200' }}>
													<Flex justifyContent="flex-start" alignItems="center">
														<Box>
															<CompanyName company={item?.company} />
															<Badge>{t(item?.report)}</Badge>
															<Badge ml={1}>{t(item?.year)}</Badge>
															<Box>
																<Badge>{t(item?.type)}</Badge>
															</Box>
														</Box>
														{transferIndex > index && (
															<Box ml={1}>
																<TransferEwsItem
																	statementId={item?.id}
																	assigneeId={transferee?.id}
																	notification={notification === 'all' ? true : false}
																	onNext={() => {
																		if (transferIndex >= list?.length) {
																			toast({
																				title: t('Completed'),
																				status: 'info',
																				isClosable: true,
																				position: 'bottom-right',
																			});
																		} else {
																			setTransferIndex(transferIndex + 1);
																		}
																	}}
																/>
															</Box>
														)}
													</Flex>
												</Box>
											))}
										</SimpleGrid>
									)}
								</>
							</Box>
						)}
					</Box>
				</Center>
			</Container>
			<SelectUserModal
				role="ews"
				isOpen={modalUser?.isOpen}
				callback={(user) => {
					if (dataUser?.getUserById?.id !== user?.id) {
						setTransferee(user);
					} else {
						toast({
							title: t('Not Allowed'),
							status: 'warning',
							isClosable: true,
							position: 'bottom',
						});
					}
				}}
				onClose={modalUser?.onClose}
			/>
		</>
	);
}

export default TransferEwsReport;
